import { inject, Injectable } from '@angular/core';
import { md5 } from '@utils/Crypto/md5.utils';
import { getCurrentCulture } from '@utils/Navigation/getCurrentCulture.utils';
import { _groupBy } from '@utils/Lodash/_groupBy.utils';
import { _mapValues } from '@utils/Lodash/_mapValues.utils';
import { parseSearch } from '@utils/Navigation/parseSearch.utils';
import { PagePaths } from '@values/Navigation/PagePaths.value';
import { ShipInfo } from '@values/Ship/ShipInfo.value';
import dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { CostaBackendApiService } from './costa-backend-api.service';
import { LoadingService } from './loading.service';
import { OtoBackendApiService } from './oto-backend-api.service';
import { OtoItineraryService } from './oto-itinerary.service';
import { ShipServiceResponse, ShipServiceShip } from '@interfaces/OTO/ShipService.interface';
import { TopShips } from '@interfaces/OTO/TopShips.interface';

@Injectable({
  providedIn: 'root',
})
export class OtoShipServiceService {
  private readonly _costaBackendApiService = inject(CostaBackendApiService);
  private readonly _loadingService = inject(LoadingService);
  private readonly _otoBackendApiService = inject(OtoBackendApiService);
  private readonly _otoItineraryService = inject(OtoItineraryService);

  private cache = {};
  public getShipService$ = new Subject<ShipServiceResponse>();
  public getCurrentShipPhotos$ = new Subject<string[]>();
  public setCurrentShipPhotos$ = new Subject();
  public setCurrentShip$ = new Subject();
  public setStrings$ = new Subject();


  public setCurrentShipPhotos(shipPhotos) {
    const key = JSON.stringify(shipPhotos);
    this.setCache(key, shipPhotos);
    setTimeout(() => {
      this.setCurrentShipPhotos$.next(this.getCache(key));
    });
  }

  public setCurrentShip(ship) {
    const key = JSON.stringify(ship);
    this.setCache(key, ship);
    setTimeout(() => {
      this.setCurrentShip$.next(this.getCache(key));
    });
  }

  public setStrings(strings) {
    const key = JSON.stringify(strings);
    this.setCache(key, strings);
    setTimeout(() => {
      this.setStrings$.next(this.getCache(key));
    });
  }

  public getCurrentShipCode() {
    return parseSearch()[ShipInfo.shipCodeLink];
  }

  public getShipService() {
    this._loadingService.startLoading('OTOShipService_getShipService');
    const key = '_getShipService';
    const cachedResult = this.getCache(key);
    if (!cachedResult) {
      this._otoBackendApiService.getShipService().then((shipService) => {
        this._otoBackendApiService.getShipsByTop(getCurrentCulture()).then((ships) => {
          const response = this.formatShipService(shipService, ships);
          this.setCache(key, response);
          this.getShipService$.next(response);
          this._loadingService.stopLoading();
        }, this.commonError);
      }, this.commonError);
    } else {
      this.getShipService$.next(cachedResult);
      this._loadingService.stopLoading('OTOShipService_getShipService');
    }
  }

  public getCurrentShipPhotos(shipName) {
    this._loadingService.startLoading('OTOShipService_getCurrentShipPhotos');
    const key = '_getCurrentShipPhotos-' + shipName;
    const cachedResult = this.getCache(key);
    if (!cachedResult) {
      this._otoBackendApiService.getCurrentShipPhotos(shipName).then(this.getCurrentShipPhotosSuccess(shipName, key), this.commonError);
    } else {
      this.getCurrentShipPhotos$.next(cachedResult);
      this._loadingService.stopLoading('OTOShipService_getCurrentShipPhotos');
    }
  }

  private getCurrentShipPhotosSuccess(shipName, key) {
    const baseUrl = 'https://www.offertetouroperator.com/images/cruises/costacrociere/' + shipName + '/';
    const returnArray = [];
    return (resp) => {
      resp.images.forEach((image) => {
        returnArray.push(baseUrl + image);
      });
      this.setCache(key, returnArray);
      this.getCurrentShipPhotos$.next(returnArray);
      this._loadingService.stopLoading();
    };
  }

  private formatShipService(shipService: ShipServiceResponse, ships: TopShips) {
    (Object.entries(shipService.ships) as [string, ShipServiceShip][]).forEach(([shipCode, ship]) => {
      const basePonti = 'https://www.offertetouroperator.com/images/cruises/costa_official/navi/';
      ship.ponti.forEach((ponte) => {
        ponte.image = basePonti + ship.nome + '/ponti/' + ponte.pos + '.jpg';
      });
      ship.shipDecks = basePonti + ship.nome + '/ponti/shipDecks.jpg';
      ship.code = shipCode;
      ship.extData = ships[shipCode];
      ship.linkDetail = `${PagePaths.shipInfoDetail}?${ShipInfo.shipCodeLink}=${shipCode}`;
    });
    return shipService;
  }

  private commonError(...args) {
    this._loadingService.stopLoading();
    // this._errorMessagesService.showErrorAlert(error);
    console.warn(...args);
  }

  private setCache(key, content) {
    const hashedKey = md5(key);
    this.cache[hashedKey] = content;
  }

  private getCache(key) {
    const hashedKey = md5(key);
    return this.cache[hashedKey];
  }
}
